import React, { useContext, useEffect, useMemo, useState } from 'react';
import { Alert, Button, Form } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faRotate, faLink, faLinkSlash } from '@fortawesome/free-solid-svg-icons';
import { useAppDispatch, useAppSelector } from '../store';
import AppContext from '../context/AppContext';
import AccountsAPI from '../api/AccountsAPI';
import { ConnectedAccountModel } from '../types/ConnectedAccountModel';
import { Platform } from '../types/Platform';
import { GroupKind } from '../types/GroupModel';
import { loadHandles } from '../reducers/settingsReducer';
import { getGames } from '../reducers/gamesReducer';
import { analytics } from '../utils/analytics';

// The "Connected accounts" tab of the Add games modal. Connect a Chess.com or Lichess handle and
// the site keeps pulling its new games into the group you choose (My Space by default), on a
// schedule, with no more clicking Sync. One handle per platform; syncs into exactly one group.
export function platformLabel(platform: Platform): string {
  return platform === Platform.Lichess ? 'Lichess' : 'Chess.com';
}

function ago(iso: string | null): string {
  if (!iso) return 'never';
  const ms = Date.now() - new Date(iso).getTime();
  const minutes = Math.round(ms / 60000);
  if (minutes < 2) return 'just now';
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 48) return `${hours} h ago`;
  return `${Math.round(hours / 24)} days ago`;
}

export default function ConnectedAccounts(): React.ReactNode {
  const dispatch = useAppDispatch();
  const { isAuthenticated, openLogin } = useContext(AppContext);
  const { groups } = useAppSelector((state) => state.groupsReducer);
  const { params } = useAppSelector((state) => state.paramsReducer);
  const [accounts, setAccounts] = useState<ConnectedAccountModel[] | null>(null);
  const [error, setError] = useState<string>();
  const [busy, setBusy] = useState<Platform | 'add' | null>(null);
  const [addPlatform, setAddPlatform] = useState<Platform>(Platform.ChessCom);
  const [addHandle, setAddHandle] = useState('');

  const mine = useMemo(
    () => groups.filter((g) => g.IsOwner && g.Kind !== GroupKind.Home),
    [groups],
  );
  const mySpace = useMemo(() => mine.find((g) => g.Kind === GroupKind.Personal), [mine]);

  useEffect(() => {
    if (!isAuthenticated) return;
    AccountsAPI.list()
      .then(setAccounts)
      .catch((e: Error) => setError(e.message));
  }, [isAuthenticated]);

  const connected = accounts ?? [];
  const free = [Platform.ChessCom, Platform.Lichess].filter(
    (p) => !connected.some((a) => a.Platform === p),
  );
  useEffect(() => {
    if (free.length > 0 && !free.includes(addPlatform)) setAddPlatform(free[0]);
  }, [free, addPlatform]);

  if (!isAuthenticated) {
    return (
      <div className="cm-accounts-signedout">
        <p>Connect your Chess.com or Lichess account and your new games arrive here on their own.</p>
        <Button variant="primary" onClick={() => openLogin?.()}>
          Sign in to connect an account
        </Button>
      </div>
    );
  }

  const connect = async () => {
    const handle = addHandle.trim();
    if (!handle) return;
    setBusy('add');
    setError(undefined);
    try {
      await AccountsAPI.connect(addPlatform, handle);
      setAccounts(await AccountsAPI.list());
      setAddHandle('');
      dispatch(loadHandles());
      dispatch(getGames(params));
      analytics.track('import_start', { ui_source: 'connect_account', platform: platformLabel(addPlatform) });
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(null);
    }
  };

  const change = async (
    platform: Platform,
    patch: {
      autoSync?: boolean;
      syncGroupId?: number | null;
      clearGroup?: boolean;
      emailNewGames?: boolean;
    },
  ) => {
    setBusy(platform);
    setError(undefined);
    try {
      setAccounts(await AccountsAPI.update(platform, patch));
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(null);
    }
  };

  const syncNow = async (platform: Platform) => {
    setBusy(platform);
    setError(undefined);
    try {
      await AccountsAPI.syncNow(platform);
      setAccounts(await AccountsAPI.list());
      dispatch(getGames(params));
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(null);
    }
  };

  const disconnect = async (account: ConnectedAccountModel) => {
    if (!window.confirm(`Disconnect ${account.Username} on ${platformLabel(account.Platform)}? Your games stay.`)) return;
    setBusy(account.Platform);
    setError(undefined);
    try {
      setAccounts(await AccountsAPI.disconnect(account.Platform));
      dispatch(loadHandles());
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="cm-accounts">
      <p className="cm-import-help">
        Connect an account and your new games are pulled in for you within minutes, into the group
        you pick, and we email you the move that cost you most in each one. My Space is the default.
        You can still add games by hand any time.
      </p>
      {error != null ? (
        <Alert variant="danger" dismissible onClose={() => setError(undefined)}>
          {error}
        </Alert>
      ) : null}

      {accounts == null ? (
        <p className="text-muted small">Loading…</p>
      ) : connected.length === 0 ? (
        <p className="text-muted small">No connected accounts yet.</p>
      ) : (
        <div className="cm-accounts-list">
          {connected.map((a) => {
            const isBusy = busy === a.Platform;
            const groupValue = a.SyncGroupId ?? mySpace?.Id ?? '';
            return (
              <div key={a.Platform} className={`cm-account ${a.AutoSync ? '' : 'is-off'}`}>
                <div className="cm-account-head">
                  <FontAwesomeIcon icon={faLink} className="cm-account-icon" fixedWidth />
                  <span className="cm-account-platform">{platformLabel(a.Platform)}</span>
                  <strong className="cm-account-handle">{a.Username}</strong>
                  <Form.Check
                    type="switch"
                    id={`autosync-${a.Platform}`}
                    className="cm-account-switch"
                    label={a.AutoSync ? 'Auto-sync on' : 'Auto-sync off'}
                    checked={a.AutoSync}
                    disabled={isBusy}
                    onChange={(e) => change(a.Platform, { autoSync: e.target.checked })}
                  />
                </div>
                <Form.Check
                  type="switch"
                  id={`emailnew-${a.Platform}`}
                  className="cm-account-switch mb-2"
                  label="Email me each new game's costliest move"
                  checked={a.EmailNewGames}
                  disabled={isBusy || !a.AutoSync}
                  onChange={(e) => change(a.Platform, { emailNewGames: e.target.checked })}
                />
                <div className="cm-account-body">
                  <Form.Label className="cm-account-label" htmlFor={`syncgroup-${a.Platform}`}>
                    Sync into
                  </Form.Label>
                  <Form.Select
                    id={`syncgroup-${a.Platform}`}
                    size="sm"
                    className="cm-account-group"
                    value={groupValue}
                    disabled={isBusy || mine.length === 0}
                    onChange={(e) => {
                      const id = Number(e.target.value);
                      const isMySpace = mySpace != null && id === mySpace.Id;
                      change(a.Platform, isMySpace ? { clearGroup: true } : { syncGroupId: id });
                    }}
                  >
                    {mine.map((g) => (
                      <option key={g.Id} value={g.Id}>
                        {g.Name}
                      </option>
                    ))}
                  </Form.Select>
                  <Button
                    size="sm"
                    variant="outline-primary"
                    className="cm-account-sync"
                    disabled={isBusy}
                    onClick={() => syncNow(a.Platform)}
                  >
                    <FontAwesomeIcon icon={faRotate} className="me-1" spin={isBusy} />
                    Sync now
                  </Button>
                  <Button
                    size="sm"
                    variant="link"
                    className="cm-account-remove"
                    disabled={isBusy}
                    onClick={() => disconnect(a)}
                  >
                    <FontAwesomeIcon icon={faLinkSlash} className="me-1" />
                    Disconnect
                  </Button>
                </div>
                <div className="cm-account-meta">
                  Last sync {ago(a.LastSyncAt)}
                  {a.LastSyncNote ? ` · ${a.LastSyncNote}` : ''}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {free.length > 0 ? (
        <form
          className="cm-account-add"
          onSubmit={(e) => {
            e.preventDefault();
            connect();
          }}
        >
          <Form.Label className="cm-account-add-label">Connect an account</Form.Label>
          <div className="cm-account-add-row">
            <Form.Select
              size="sm"
              value={addPlatform}
              disabled={busy === 'add'}
              onChange={(e) => setAddPlatform(Number(e.target.value) as Platform)}
              aria-label="Site"
            >
              {free.map((p) => (
                <option key={p} value={p}>
                  {platformLabel(p)}
                </option>
              ))}
            </Form.Select>
            <Form.Control
              size="sm"
              name="handle"
              value={addHandle}
              placeholder={`Your ${platformLabel(addPlatform)} handle`}
              disabled={busy === 'add'}
              autoComplete="off"
              autoCorrect="off"
              autoCapitalize="none"
              spellCheck={false}
              data-lpignore="true"
              data-1p-ignore="true"
              onChange={(e) => setAddHandle(e.target.value)}
            />
            <Button size="sm" type="submit" variant="primary" disabled={busy === 'add' || !addHandle.trim()}>
              {busy === 'add' ? 'Connecting…' : 'Connect'}
            </Button>
          </div>
          <div className="text-muted small">
            The first sync brings in the last 30 days. After that, new games within minutes.
          </div>
        </form>
      ) : null}
    </div>
  );
}
